import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import OutlinedInput from "@mui/material/OutlinedInput";
import Alert from "@mui/material/Alert";

import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

import { ButtonUI, KMKDrawer } from "../../../../Common";
import RuleGroup from "./RuleGroup";

import { ruleTypes } from "../data";

import { showToast } from "../../../../../store/actions";

const drawerWidth = 420;

const useStyles = makeStyles()((theme) => ({
  heading: {
    color: "#DF672A",
    fontWeight: "bold",
    fontSize: "1rem",
    "& span": {
      fontWeight: "normal",
    },
  },
  container: {
    padding: "0px 30px 20px 20px",
    "& .errorMessage .MuiPaper-root": {
      marginTop: "20px",
      alignItems: "flex-start",
      border: "none",
    },
    "& .errorMessage .MuiAlert-message": {
      color: "#C72C2C",
    },
    "& .errorMessage": {
      marginBottom: 0,
    },
  },
}));

const RuleEditor = ({ onClose, handleSave, rule, fields, sources: dataSources, rules }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [name, setName] = useState(rule.name || "");
  const [nameError, setNameError] = useState("");
  const [ruleType, setRuleType] = useState(rule.type || "auto");
  const [sources, setSources] = useState(
    rule?.sources?.length === 1 && rule.sources[0] === "Any" ? dataSources.map(({ id }) => id) : rule?.sources || []
  );
  const [enabled, setEnabled] = useState(rule.enabled ?? true);
  const [selectAll, setSelectAll] = useState((rule?.sources?.length === 1 && rule.sources[0] === "Any") || false);

  const [groups, setGroups] = useState(
    rule.groups || [
      {
        fields: [],
        method: "eq",
        threshold: "90",
      },
    ]
  );

  const handleAddGroup = useCallback(() => {
    setGroups((prev) => [...prev, { fields: [], method: "eq", threshold: "90" }]);
  }, []);

  const handleSelectAll = useCallback(
    (e) => {
      let isChecked = e.target.checked;
      setSelectAll(isChecked);
      if (isChecked) setSources(dataSources.map(({ id }) => id));
      else setSources([]);
    },
    [dataSources]
  );

  const handleSaveRule = useCallback(() => {
    if (name.length < 2 || name.length > 50) setNameError("Rule name must be between 2 to 50 characters.");
    else if (
      rules.find(({ name: rName }) => name.toLowerCase() === rName.toLowerCase()) &&
      rules.find(({ name: rName }) => name.toLowerCase() === rName.toLowerCase()).id !== rule.id
    )
      setNameError("Rule name must be Unique.");
    else if (groups.every(({ method }) => method === "isn"))
      dispatch(
        showToast({ message: `"isNull" requires a paired grouping; another group must be defined.`, type: "error" })
      );
    else {
      setNameError("");
      handleSave(
        {
          ...(rule?.id ? { id: rule.id, prevRuleType: rule.type } : {}),
          name,
          ruleType,
          sources,
          enabled,
          groups,
          selectAll,
        },
        !rule
      );
    }
  }, [dispatch, enabled, groups, handleSave, name, rule, ruleType, rules, selectAll, sources]);

  const $disbledSaveButton = useMemo(() => {
    return (
      !name ||
      !ruleType ||
      !sources ||
      sources.length === 0 ||
      !groups ||
      groups.length === 0 ||
      groups.some(
        ({ fields = [], method, threshold = 90 }) =>
          fields.length === 0 ||
          !method ||
          (method === "jw" && (!threshold || threshold <= 0 || threshold >= 100)) ||
          (method === "isn" && fields.length >= 2)
      )
    );
  }, [groups, name, ruleType, sources]);

  return (
    <KMKDrawer
      anchor="right"
      open={true}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: { sm: "100%", md: "500px" },
          boxSizing: "border-box",
        },
      }}
    >
      <Box display="flex" flexDirection="column" sx={{ height: "100vh" }}>
        <Toolbar />
        <Box sx={{ padding: "0px 5px 20px 20px", display: "flex" }}>
          <Box flexGrow={1}>
            <Typography className={classes.heading} variant="h6" gutterBottom component="div">
              Match Rule
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className={classes.container}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                label="Name"
                variant="outlined"
                inputProps={{ maxLength: 50 }}
                autoFocus={!rule}
                error={nameError !== ""}
                helperText={nameError}
                required
              />
            </Grid>
            <Grid size={6}>
              <FormControl variant="outlined" required fullWidth>
                <InputLabel>Type</InputLabel>
                <Select label="Type" value={ruleType} onChange={(e) => setRuleType(e.target.value)}>
                  {ruleTypes.map(({ id, label }) => (
                    <MenuItem key={id} value={id}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControlLabel
                sx={{ color: "black" }}
                control={<Checkbox size="medium" checked={enabled} />}
                onChange={(e) => setEnabled((prev) => !prev)}
                label="Enabled"
                labelPlacement="end"
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Sources</InputLabel>
                <Select
                  multiple
                  value={sources}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value.find((v) => v === "select all")) return;
                    setSources(value);
                    setSelectAll(value.length === dataSources.length);
                  }}
                  input={<OutlinedInput label="Sources" />}
                  renderValue={(selected) =>
                    selectAll
                      ? "Any"
                      : selected
                          .map((seletedDS) => dataSources.find(({ id }) => id === seletedDS))
                          .map(({ name }) => name)
                          .join(",")
                  }
                >
                  <MenuItem value="select all">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectAll || dataSources.length === sources.length}
                          indeterminate={sources.length !== 0 && dataSources.length !== sources.length}
                          onChange={handleSelectAll}
                        />
                      }
                      label="Any"
                      className={classes.menuItem}
                    />
                  </MenuItem>
                  {dataSources.map(({ id, name }) => (
                    <MenuItem key={id} value={id}>
                      <Checkbox checked={sources.indexOf(id) > -1} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.container} flexGrow={1} sx={{ flex: "1 1 auto", overflowY: "auto", height: "0px" }}>
          <Grid container spacing={2}>
            <Alert severity="info" sx={{ width: "100%" }}>
              Groups are combined using AND logic.
            </Alert>
            {groups.map((group, index) => (
              <Grid key={index} size={12}>
                <RuleGroup
                  key={index}
                  index={index}
                  group={group}
                  setGroups={setGroups}
                  fields={fields}
                  groups={groups}
                />
              </Grid>
            ))}
            <Grid sx={{ textAlign: "right" }} size={12}>
              <ButtonUI endIcon={<AddIcon />} variant="contained" color="info" onClick={handleAddGroup}>
                Add Group
              </ButtonUI>
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.container} sx={{ textAlign: "right" }}>
          <Box my={2}>
            <ButtonUI variant="contained" color="success" onClick={handleSaveRule} disabled={$disbledSaveButton}>
              Save
            </ButtonUI>
            <ButtonUI variant="contained" color="secondary" style={{ marginLeft: 15 }} onClick={onClose}>
              Cancel
            </ButtonUI>
          </Box>
        </Box>
      </Box>
    </KMKDrawer>
  );
};

export default RuleEditor;
